// src/pages/team/TeamOverviewTab.tsx
// Hub landing for the team workspace: real activity feed, counters, on-duty,
// team schedule and quick actions. Replaces the placeholder-data Activity tab.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, Badge, Button } from '@react/components';
import { teamApi } from '@core/api';
import type { TeamAuditLog } from '@core/types/team/TeamAuditLog';
import type { RoleSchedule } from '@core/types/team/RoleSchedule';
import type { GetCurrentScheduleAssigneeResponse } from '@core/types/team/GetCurrentScheduleAssigneeResponse';
import type { TeamCounters } from '@core/types/team/TeamCounters';
import { useScope } from '@/hooks/useScope';
import { usePageActions } from '@/hooks/usePageActions';
import { EntitySchedulePanel } from '@/features/calendar/EntitySchedulePanel';
import { makeEntity } from '@/features/calendar/time';
import {
  formatRelativeTime,
  getInitials,
  getAvatarColor,
} from '@/features/team/logic';
import type { TeamTab } from '@/features/team/logic';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

const ICONS = {
  task: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  document: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  discussion: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  invite: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  link: 'M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244',
  activity: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  shield: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
} as const;

function ActionIcon({ path, className }: { path: string; className?: string }) {
  return (
    <svg className={className ?? 'h-4 w-4'} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Activity feed (real data: team audit logs)
// ---------------------------------------------------------------------------

const AUDIT_ACTION_LABEL_KEYS: Record<string, string> = {
  create: 'team.overview.activity.create',
  update: 'team.overview.activity.update',
  delete: 'team.overview.activity.delete',
  add: 'team.overview.activity.add',
  remove: 'team.overview.activity.remove',
  assign: 'team.overview.activity.assign',
  revoke: 'team.overview.activity.revoke',
};

function activityLabelKey(action: string): string | null {
  const normalized = action.toLowerCase();
  for (const [needle, key] of Object.entries(AUDIT_ACTION_LABEL_KEYS)) {
    if (normalized.includes(needle)) return key;
  }
  return null;
}

const ActivityFeed = observer(({ teamId }: { teamId: string }) => {
  const { t, locale } = useI18n();
  const [logs, setLogs] = useState<TeamAuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(false);
    teamApi
      .ListTeamLogs(teamId, {
        pagination: { cursor: '', limit: 10, direction: 'forward' },
        sort: [],
        view: 0,
      } as never)
      .then((res) => { if (alive) setLogs(res.logs?.logs ?? []); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setIsLoading(false); });
    return () => { alive = false; };
  }, [teamId]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('team.overview.activityTitle')}</CardTitle>
        <Link to={`/teams/${teamId}/audit`} className="text-xs text-primary hover:underline">
          {t('common.viewAll')}
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="h-8 w-8 rounded-full bg-muted" />
                <div className="h-4 flex-1 rounded bg-muted" />
                <div className="h-3 w-12 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="text-sm text-muted-foreground py-4">{t('team.overview.activityError')}</p>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <ActionIcon path={ICONS.activity} className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">{t('team.overview.activityEmpty')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('team.overview.activityEmptyHint')}</p>
          </div>
        ) : (
          <ul className="space-y-1">
            {logs.map((log, i) => {
              const actorName = log.actor?.display_name || '--';
              const labelKey = activityLabelKey(log.action || '');
              return (
                <li key={log.entity?.id || i} className="flex items-center gap-3 py-1.5">
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: getAvatarColor(actorName) }}
                    aria-hidden="true"
                  >
                    {getInitials(actorName)}
                  </div>
                  <p className="flex-1 min-w-0 text-sm text-foreground truncate">
                    <span className="font-medium">{actorName}</span>{' '}
                    <span className="text-muted-foreground">
                      {labelKey ? t(labelKey) : log.action}
                      {log.target_type ? ` · ${log.target_type}` : ''}
                    </span>
                  </p>
                  <time className="flex-shrink-0 text-xs text-muted-foreground" dateTime={log.timestamp}>
                    {formatRelativeTime(log.timestamp, locale)}
                  </time>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
});

// ---------------------------------------------------------------------------
// On-duty card — first active rotation schedule
// ---------------------------------------------------------------------------

const OnDutyCard = observer(({ teamId }: { teamId: string }) => {
  const { t, locale } = useI18n();
  const [schedule, setSchedule] = useState<RoleSchedule | null>(null);
  const [assignee, setAssignee] = useState<GetCurrentScheduleAssigneeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    teamApi
      .ListRoleSchedules(teamId, {
        pagination: { cursor: '', limit: 20, direction: 'forward' },
        sort: [],
        view: 0,
      } as never)
      .then(async (res) => {
        const active = (res.schedules?.schedules ?? []).find(
          (s) => s.status === 'ROLE_SCHEDULE_STATUS_ACTIVE',
        );
        if (!alive) return;
        setSchedule(active ?? null);
        if (active?.entity?.id) {
          const cur = await teamApi
            .GetCurrentScheduleAssignee(teamId, active.entity.id)
            .catch(() => null);
          if (alive) setAssignee(cur);
        }
      })
      .catch(() => { /* no schedules — card hides itself */ })
      .finally(() => { if (alive) setIsLoading(false); });
    return () => { alive = false; };
  }, [teamId]);

  // No on-call setup — don't take space on the overview.
  if (!isLoading && !schedule) return null;

  const name =
    assignee?.assignee?.actor?.display_name ||
    (assignee?.assignee as { profile?: { name?: string } } | undefined)?.profile?.name ||
    null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('team.overview.onDutyTitle')}</CardTitle>
        <Link to={`/teams/${teamId}/schedules`} className="text-xs text-primary hover:underline">
          {t('common.viewAll')}
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-3 animate-pulse" aria-hidden="true">
            <div className="h-9 w-9 rounded-full bg-muted" />
            <div className="space-y-1.5 flex-1">
              <div className="h-4 w-28 rounded bg-muted" />
              <div className="h-3 w-40 rounded bg-muted" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
              style={{ backgroundColor: getAvatarColor(name || '?') }}
              aria-hidden="true"
            >
              {getInitials(name || '?')}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {name || t('team.overview.onDutyNobody')}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {schedule?.name}
                {assignee?.next_rotation_at
                  ? ` · ${t('team.overview.nextRotation')} ${formatRelativeTime(assignee.next_rotation_at, locale)}`
                  : ''}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

// ---------------------------------------------------------------------------
// Main overview tab
// ---------------------------------------------------------------------------

interface TeamOverviewTabProps {
  teamId: string;
  team: { entity?: { id: string } } | null;
  onNavigateTab: (tab: TeamTab, extra?: Record<string, string>) => void;
}

export const TeamOverviewTab = observer(({ teamId, onNavigateTab }: TeamOverviewTabProps) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const [counters, setCounters] = useState<TeamCounters | null>(null);
  const [canManage, setCanManage] = useState(false);

  useEffect(() => {
    let alive = true;
    teamApi
      .GetTeamDetails(teamId)
      .then((res) => {
        if (!alive) return;
        setCounters(res.counters ?? null);
        setCanManage(Boolean(res.permissions?.can_approve_members || res.permissions?.can_invite_members));
      })
      .catch(() => { /* counters are progressive enhancement */ });
    return () => { alive = false; };
  }, [teamId]);

  const teamEntity = useMemo(() => makeEntity(teamId, 'team', scope), [teamId, scope]);

  // Page quick actions live in the right drawer, not inline on the page.
  usePageActions([
    {
      key: 'task',
      icon: ICONS.task,
      label: t('team.overview.actionNewTask'),
      onSelect: () => navigate(`/tasks/new?parent_id=${teamId}&parent_type=team`),
    },
    {
      key: 'document',
      icon: ICONS.document,
      label: t('team.overview.actionNewDocument'),
      onSelect: () => navigate(`/documents/new?parent_id=${teamId}&parent_type=team`),
    },
    {
      key: 'discussion',
      icon: ICONS.discussion,
      label: t('team.overview.actionNewDiscussion'),
      onSelect: () => onNavigateTab('discussions', { new: '1' }),
    },
    {
      key: 'invite',
      icon: ICONS.invite,
      label: t('team.overview.actionInvite'),
      onSelect: () => navigate(`/teams/${teamId}/members?tab=invitations`),
    },
    {
      key: 'resource',
      icon: ICONS.link,
      label: t('team.overview.actionLinkResource'),
      onSelect: () => onNavigateTab('resources'),
    },
  ]);

  // Server counters omit zero values (omitempty) — once the object arrived,
  // a missing field means 0, not "unknown".
  const stat = (v: number | undefined) => (counters ? v ?? 0 : undefined);
  const stats = [
    {
      key: 'members',
      value: stat(counters?.members_count),
      label: t('team.overview.statMembers'),
      onClick: () => onNavigateTab('people'),
    },
    {
      key: 'roles',
      value: stat(counters?.roles_count),
      label: t('team.overview.statRoles'),
      onClick: () => navigate(`/teams/${teamId}/roles`),
    },
    {
      key: 'invitations',
      value: stat(counters?.invitations_count),
      label: t('team.overview.statInvitations'),
      onClick: () => navigate(`/teams/${teamId}/members?tab=invitations`),
    },
    {
      key: 'pending',
      value: stat(counters?.pending_requests_count),
      label: t('team.overview.statPending'),
      onClick: () => navigate(`/teams/${teamId}/applications`),
    },
  ];

  const pendingCount = counters?.pending_requests_count ?? 0;

  return (
    <div className="space-y-6">
      {/* Stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={s.onClick}
            className="rounded-xl border border-border bg-card px-4 py-3 text-left hover:border-primary/40 hover:bg-muted/30 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <p className="text-2xl font-bold text-foreground tabular-nums">
              {s.value ?? '–'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Attention card for admins */}
      {canManage && pendingCount > 0 && (
        <div className="rounded-xl border border-warning/40 bg-warning/5 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <ActionIcon path={ICONS.shield} className="h-5 w-5 text-warning flex-shrink-0" />
            <p className="text-sm text-foreground truncate">
              {t('team.overview.attentionPending')}{' '}
              <Badge variant="warning" size="sm">{pendingCount}</Badge>
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => navigate(`/teams/${teamId}/applications`)}>
            {t('team.overview.attentionReview')}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left: activity */}
        <div className="lg:col-span-2 space-y-6">
          <ActivityFeed teamId={teamId} />
        </div>

        {/* Right: on-duty, schedule (quick actions live in the right drawer) */}
        <div className="space-y-6">
          <OnDutyCard teamId={teamId} />

          <Card>
            <CardHeader>
              <CardTitle>{t('team.overview.scheduleTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntitySchedulePanel entity={teamEntity} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
});

export default TeamOverviewTab;
